import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { 
  TrendingUp, Building2, Landmark, Coins, BadgePercent,
  PiggyBank, Briefcase, Users, LineChart,
  ChevronLeft, ChevronRight, FileText, Calculator, BarChart3, PieChart, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

// Real asset data from the user's portfolio
const assetData = [
  { 
    name: 'Mutual Funds', 
    icon: TrendingUp, 
    currentValue: '₹63L', 
    invested: '₹53.24L',
    gains: '₹9.75L',
    percentage: '40%',
    xirr: '10.71%',
    color: 'tech-blue',
    isPositive: true
  },
  { 
    name: 'Fixed Deposits', 
    icon: Landmark, 
    currentValue: '₹40.9L', 
    invested: '₹40L',
    gains: '₹0.9L',
    percentage: '26%',
    xirr: '6.71%',
    color: 'wealth-green',
    isPositive: true
  },
  { 
    name: 'EPF', 
    icon: Building2, 
    currentValue: '₹27.85L', 
    invested: '₹22.54L',
    gains: '₹5.31L',
    percentage: '18%',
    xirr: '8.25%',
    color: 'electric-cyan',
    isPositive: true
  },
  { 
    name: 'Gold & Silver', 
    icon: Coins, 
    currentValue: '₹13.24L', 
    invested: '₹7.05L',
    gains: '₹6.18L',
    percentage: '8%',
    xirr: '54.29%',
    color: 'missed-gold',
    isPositive: true,
    highlight: true
  },
  { 
    name: 'NPS', 
    icon: BadgePercent, 
    currentValue: '₹10.8L', 
    invested: '₹9.91L',
    gains: '₹0.89L',
    percentage: '7%',
    xirr: '6.40%',
    color: 'alert-red',
    isPositive: true
  },
];

const totalPortfolio = {
  currentValue: '₹1.56 Cr',
  invested: '₹1.33 Cr',
  gains: '₹23.1L',
  gainsPercent: '14.8%',
  xirr: '12.63%',
  todaysGain: '₹68.2K',
  todaysGainPercent: '0.44%'
};

const allAssets = [
  'Mutual Funds', 'Stocks', 'Real Estate', 'Gold & Silver', 'Fixed Deposits', 
  'EPF', 'NPS', 'PPF', 'SSY', 'Bonds', 'RSU', 'Savings A/C', 'RD'
];

// Dashboard screenshots gallery
const dashboardScreenshots = [
  {
    title: 'Overall Portfolio Summary',
    description: 'Complete overview of all your investments with real-time values, gains, and XIRR across all asset classes',
    image: 'https://customer-assets.emergentagent.com/job_0e106ead-99c1-4282-a843-527b7f3bc3e4/artifacts/5qyll5jq_overall%20portfolio_1.png',
    icon: PieChart
  },
  {
    title: 'Portfolio Investment Summary',
    description: 'Detailed mutual fund analysis with individual fund performance and daily tracking',
    image: 'https://customer-assets.emergentagent.com/job_3a3d18e0-0cd7-4197-a95a-f3190922629f/artifacts/xe7vwcuz_1.%20Portfolio%20Investment%20summary.png',
    icon: BarChart3
  },
  {
    title: 'Portfolio vs Benchmark',
    description: 'Compare your exact investments against Nifty 50, Midcap, Gold and more using your actual dates',
    image: 'https://customer-assets.emergentagent.com/job_3a3d18e0-0cd7-4197-a95a-f3190922629f/artifacts/zu7pqlrb_2.%20Portfolio%20vs%20Benachmark.png',
    icon: TrendingUp
  },
  {
    title: 'Transaction Summary',
    description: 'Track your SIP discipline and investment journey over the years',
    image: 'https://customer-assets.emergentagent.com/job_3a3d18e0-0cd7-4197-a95a-f3190922629f/artifacts/uenviig1_4.Transaction%20Summary.png',
    icon: FileText
  },
  {
    title: 'Tax Report',
    description: 'FY-wise capital gains calculation for all your redemptions',
    image: 'https://customer-assets.emergentagent.com/job_3a3d18e0-0cd7-4197-a95a-f3190922629f/artifacts/7bvdnbci_3.Tax%20report.png',
    icon: Calculator
  },
  {
    title: 'NAV Purchase Analysis',
    description: 'See how current NAV compares to your average purchase NAV for lumpsum decisions',
    image: 'https://customer-assets.emergentagent.com/job_3a3d18e0-0cd7-4197-a95a-f3190922629f/artifacts/fsge2230_5.NAV%20Purchase%20analysis.png',
    icon: LineChart
  }
];

const MultiAssetBento = () => {
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  
  const nextScreenshot = () => {
    setActiveScreenshot((prev) => (prev + 1) % dashboardScreenshots.length);
  };
  
  const prevScreenshot = () => {
    setActiveScreenshot((prev) => (prev - 1 + dashboardScreenshots.length) % dashboardScreenshots.length);
  };

  return (
    <section className="relative py-24 lg:py-32 bg-deep-space overflow-hidden" data-testid="multi-asset-bento">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header with Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="font-outfit text-3xl lg:text-5xl font-bold text-white tracking-tight">
              Why Stop At Mutual Funds?
            </h2>
          </div>
          
          {/* The Story */}
          <div className="max-w-4xl mx-auto bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8">
            <p className="text-slate-300 font-ibm-plex leading-relaxed text-lg">
              Once I had clarity on my mutual fund portfolio, a new question emerged: 
              <span className="text-white font-medium"> "What about my FDs, EPF, Gold, NPS?"</span>
            </p>
            <p className="text-slate-300 font-ibm-plex leading-relaxed mt-4">
              We all have investments scattered across multiple platforms — 
              bank FDs, office EPF, SGB from RBI, NPS from PFRDA. 
              <span className="text-missed-gold font-medium"> How do I know my overall wealth? My cumulative XIRR? How much am I actually growing each day?</span>
            </p>
            <p className="text-slate-300 font-ibm-plex leading-relaxed mt-4">
              So I extended this tool to track <span className="text-white font-medium">ALL assets in one unified dashboard</span> — 
              giving me a complete picture of my financial life. Now I know exactly where I stand, every single day.
            </p>
          </div>
        </motion.div>
        
        {/* Asset Marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <div className="bg-[#f5f3e8] py-4 -mx-6 lg:-mx-8">
            <Marquee gradient={false} speed={40} className="overflow-hidden">
              {allAssets.concat(allAssets).map((asset, index) => (
                <span key={index} className="mx-8 font-outfit text-lg font-semibold text-[#3d3b30] whitespace-nowrap">
                  {asset}
                </span>
              ))}
            </Marquee>
          </div>
        </motion.div>
        
        {/* Total Portfolio Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="bg-gradient-to-r from-tech-blue/20 to-wealth-green/20 backdrop-blur-xl border border-white/20 rounded-2xl p-6 lg:p-8">
            <div className="text-center mb-6">
              <h3 className="font-outfit text-xl font-bold text-white">My Complete Portfolio</h3>
              <p className="text-slate-400 text-sm font-ibm-plex">All assets combined • Updated daily</p>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
              <div className="text-center">
                <div className="font-jetbrains text-3xl lg:text-4xl font-bold text-white">{totalPortfolio.currentValue}</div>
                <div className="text-slate-400 text-sm font-ibm-plex">Current Value</div>
              </div>
              <div className="text-center">
                <div className="font-jetbrains text-3xl lg:text-4xl font-bold text-slate-300">{totalPortfolio.invested}</div>
                <div className="text-slate-400 text-sm font-ibm-plex">Invested</div>
              </div>
              <div className="text-center">
                <div className="font-jetbrains text-3xl lg:text-4xl font-bold text-wealth-green">{totalPortfolio.gains}</div>
                <div className="text-slate-400 text-sm font-ibm-plex">Unrealized Gains ({totalPortfolio.gainsPercent})</div>
              </div>
              <div className="text-center">
                <div className="font-jetbrains text-3xl lg:text-4xl font-bold text-wealth-green">{totalPortfolio.todaysGain}</div>
                <div className="text-slate-400 text-sm font-ibm-plex">Today's Gain ({totalPortfolio.todaysGainPercent})</div>
              </div>
              <div className="text-center bg-wealth-green/20 rounded-xl p-3">
                <div className="font-jetbrains text-3xl lg:text-4xl font-bold text-wealth-green">{totalPortfolio.xirr}</div>
                <div className="text-white text-sm font-ibm-plex font-medium">Overall XIRR</div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Asset-wise Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <h3 className="font-outfit text-2xl font-bold text-white mb-6 text-center">Asset-wise Performance</h3>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {assetData.map((asset, index) => {
              const Icon = asset.icon;
              return (
                <motion.div
                  key={asset.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className={`
                    bg-white/5 backdrop-blur-xl border rounded-2xl p-5
                    ${asset.highlight ? 'border-missed-gold/40 bg-missed-gold/10' : 'border-white/10'}
                    hover:bg-white/10 transition-colors duration-300
                  `}
                >
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`
                      w-10 h-10 rounded-xl flex items-center justify-center
                      ${asset.color === 'tech-blue' ? 'bg-tech-blue/20' :
                        asset.color === 'wealth-green' ? 'bg-wealth-green/20' :
                        asset.color === 'electric-cyan' ? 'bg-cyan-500/20' :
                        asset.color === 'missed-gold' ? 'bg-missed-gold/20' :
                        'bg-red-500/20'}
                    `}>
                      <Icon className={`w-5 h-5 ${
                        asset.color === 'tech-blue' ? 'text-tech-blue' :
                        asset.color === 'wealth-green' ? 'text-wealth-green' :
                        asset.color === 'electric-cyan' ? 'text-cyan-400' :
                        asset.color === 'missed-gold' ? 'text-missed-gold' :
                        'text-red-400'
                      }`} />
                    </div>
                    <div>
                      <div className="font-ibm-plex text-white font-medium text-sm">{asset.name}</div>
                      <div className="text-slate-400 text-xs">{asset.percentage} of portfolio</div>
                    </div>
                  </div>
                  
                  {/* Values */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-xs">Current</span>
                      <span className="font-jetbrains text-white font-bold">{asset.currentValue}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-xs">Invested</span>
                      <span className="font-jetbrains text-slate-300 text-sm">{asset.invested}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-xs">Gains</span>
                      <span className={`font-jetbrains text-sm flex items-center gap-1 ${asset.isPositive ? 'text-wealth-green' : 'text-alert-red'}`}>
                        {asset.isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {asset.gains}
                      </span>
                    </div>
                  </div>
                  
                  {/* XIRR */}
                  <div className={`
                    mt-4 pt-3 border-t border-white/10 text-center
                  `}>
                    <div className={`font-jetbrains text-xl font-bold ${
                      asset.highlight ? 'text-missed-gold' :
                      asset.color === 'tech-blue' ? 'text-tech-blue' :
                      asset.color === 'wealth-green' ? 'text-wealth-green' :
                      asset.color === 'electric-cyan' ? 'text-cyan-400' :
                      'text-red-400'
                    }`}>
                      {asset.xirr}
                    </div>
                    <div className="text-slate-400 text-xs">XIRR</div>
                    {asset.highlight && (
                      <div className="text-missed-gold text-xs mt-1 font-medium">Best performer!</div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
        
        {/* Real Dashboard Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="text-center mb-8">
            <h3 className="font-outfit text-2xl lg:text-3xl font-bold text-white">See It In Action</h3>
            <p className="text-slate-400 font-ibm-plex mt-2">Real screenshots from the Portfolio Auditor desktop tool</p>
          </div>
          
          {/* Screenshot Gallery */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            {/* Gallery Header */}
            <div className="bg-deep-space-surface border-b border-white/10 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-alert-red/60" />
                  <div className="w-3 h-3 rounded-full bg-missed-gold/60" />
                  <div className="w-3 h-3 rounded-full bg-wealth-green/60" />
                </div>
                <span className="text-sm text-slate-400 font-ibm-plex ml-2">
                  {dashboardScreenshots[activeScreenshot].title}
                </span>
              </div>
              
              {/* Navigation */}
              <div className="flex items-center gap-2">
                <button 
                  onClick={prevScreenshot}
                  className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  aria-label="Previous screenshot"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <span className="text-sm text-slate-400 font-ibm-plex px-2">
                  {activeScreenshot + 1} / {dashboardScreenshots.length}
                </span>
                <button 
                  onClick={nextScreenshot}
                  className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  aria-label="Next screenshot"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
            
            {/* Screenshot Display */}
            <div className="relative">
              <img 
                src={dashboardScreenshots[activeScreenshot].image}
                alt={dashboardScreenshots[activeScreenshot].title}
                className="w-full h-auto"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-deep-space via-transparent to-transparent pointer-events-none" />
            </div>
            
            {/* Screenshot Description */}
            <div className="bg-deep-space-surface/50 border-t border-white/10 px-6 py-4">
              <p className="text-slate-300 font-ibm-plex text-center">
                {dashboardScreenshots[activeScreenshot].description}
              </p>
            </div>
          </div>
          
          {/* Thumbnail Navigation */}
          <div className="flex justify-center gap-3 mt-6 flex-wrap">
            {dashboardScreenshots.map((screenshot, index) => {
              const Icon = screenshot.icon;
              return (
                <button
                  key={index}
                  onClick={() => setActiveScreenshot(index)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-ibm-plex transition-all
                    ${activeScreenshot === index 
                      ? 'bg-tech-blue text-white' 
                      : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white border border-white/10'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{screenshot.title}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
        
        {/* Key Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {[
            { icon: LineChart, title: 'Daily Portfolio Value', desc: 'See how your wealth grows each day' },
            { icon: Briefcase, title: 'Cumulative XIRR', desc: 'Know your true overall returns' },
            { icon: Users, title: 'Family Portfolio', desc: 'Track up to 3 PANs combined' },
            { icon: PiggyBank, title: 'Asset Allocation', desc: 'Visualize your wealth distribution' },
          ].map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors">
                <Icon className="w-6 h-6 text-tech-blue mb-3" />
                <div className="font-ibm-plex text-white font-medium">{feature.title}</div>
                <div className="text-sm text-slate-400 mt-1">{feature.desc}</div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default MultiAssetBento;
